import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from "@angular/core";
import { IBpTooltipPosition } from "./bp-tooltip.interface";

@Directive({
	selector: "[bp-tooltip], [bpTooltip]",
	standalone: false
})
export class BpTooltipDirective implements OnDestroy {
	@Input("bp-tooltip") public tooltip = "";
	@Input() public position: IBpTooltipPosition = "right";
	@Input() public delay = 0;

	private tooltipEl: HTMLElement | null = null;
	// Distance between host element and tooltip element
	private offset = 10;

	public constructor(
		private el: ElementRef,
		private renderer: Renderer2
	) {}

	@HostListener("mouseenter")
	onMouseEnter(): void {
		if (!this.tooltipEl && !!this.tooltip) {
			this.show();
		}
	}

	@HostListener("mouseleave")
	onMouseLeave(): void {
		if (this.tooltipEl) {
			this.hide();
		}
	}

	public show(): void {
		this.create();
		this.setPosition();
		this.renderer.addClass(this.tooltipEl, "bp-tooltip-show");
	}

	public hide(): void {
		this.renderer.removeClass(this.tooltipEl, "bp-tooltip-show");
		window.setTimeout(() => {
			this.renderer.removeChild(document.body, this.tooltipEl);
			this.tooltipEl = null;
		}, this.delay);
	}

	public create(): void {
		this.tooltipEl = this.renderer.createElement("span");

		this.renderer.appendChild(
			this.tooltipEl,
			this.renderer.createText(this.tooltip) // TextNode
		);

		this.renderer.appendChild(document.body, this.tooltipEl);
		// This.renderer.appendChild(this.el.nativeElement, this.tooltip);

		this.renderer.addClass(this.tooltipEl, "bp-tooltip");
		this.renderer.addClass(this.tooltipEl, `bp-tooltip-${this.position}`);

		// Delay
		this.renderer.setStyle(this.tooltipEl, "transition", `opacity ${this.delay}ms`);
	}

	private setPosition(): void {
		// Host element size and position
		const hostPos = this.el.nativeElement.getBoundingClientRect();

		// Tooltip element size and position
		const tooltipPos = this.tooltipEl?.getBoundingClientRect();

		// Returns the relative position in the viewport
		// When scrolling occurs, the vertical scroll coordinate value should be reflected on the top of the tooltip element
		const scrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

		let top;
		let left;

		if (tooltipPos) {
			if (this.position === "top") {
				top = hostPos.top - tooltipPos.height - this.offset;
				left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
			}

			if (this.position === "bottom") {
				top = hostPos.bottom + this.offset;
				left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
			}

			if (this.position === "left") {
				top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
				left = hostPos.left - tooltipPos.width - this.offset;
			}

			if (this.position === "right") {
				top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
				left = hostPos.right + this.offset;
			}
		}

		// When scrolling occurs, the vertical scroll coordinate value should be reflected on the top of the tooltip element.
		this.renderer.setStyle(this.tooltipEl, "top", `${top + scrollPos}px`);
		this.renderer.setStyle(this.tooltipEl, "left", `${left}px`);
	}

	public ngOnDestroy(): void {
		if (this.tooltipEl) {
			this.hide();
		}
	}
}
